import { Columns3, ChevronDown, Plus, Trash2, X, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isLightHex } from "./helpers";
import { MAX_PINS } from "./usePinnedShades";

function PinCard({ pin, onRemove }) {
  const light = isLightHex(pin.colour_hex);
  return (
    <div
      className="shrink-0 w-32 rounded-sm overflow-hidden border border-[var(--mp-line)]"
      data-testid={`compare-card-${pin.key}`}
    >
      <div
        className="relative h-28 flex items-end justify-between p-2"
        style={{ backgroundColor: pin.colour_hex }}
      >
        <span
          className={`text-sm font-bold ${light ? "text-gray-900" : "text-white"}`}
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          #{pin.colour_number}
        </span>
        <button
          onClick={() => onRemove(pin.key)}
          className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center"
          style={{ backgroundColor: "rgba(16,16,16,0.55)" }}
          aria-label={`Unpin shade ${pin.colour_number}`}
          data-testid={`compare-remove-${pin.key}`}
        >
          <X className="w-3 h-3 text-white" />
        </button>
      </div>
      <div className="px-2.5 py-2" style={{ backgroundColor: "var(--mp-card)" }}>
        <p
          className="text-[11px] font-medium truncate"
          style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}
        >
          {pin.colour_family}
        </p>
        <p className="text-[10px] truncate" style={{ color: "var(--mp-muted)" }}>
          {pin.fabric_name}
        </p>
        <p className="text-[10px] mt-0.5" style={{ color: "var(--mp-copper)" }}>
          {pin.colour_hex.toUpperCase()}
        </p>
      </div>
    </div>
  );
}

export default function CompareTray({
  pinned,
  open,
  onToggleOpen,
  onRemove,
  onClear,
  onAddAll,
  onShare,
  sharing = false,
  shareCard = null,
  shareError = "",
}) {
  if (!pinned.length) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 pointer-events-none">
      <div
        className="max-w-5xl mx-auto rounded-sm border shadow-2xl pointer-events-auto overflow-hidden"
        style={{
          backgroundColor: "var(--mp-card)",
          borderColor: "var(--mp-copper)",
        }}
        data-testid="compare-tray"
      >
        <button
          onClick={onToggleOpen}
          className="w-full flex items-center justify-between px-5 py-3"
          style={{ backgroundColor: "var(--mp-surface)" }}
          data-testid="compare-tray-toggle"
        >
          <span className="flex items-center gap-3">
            <Columns3 className="w-4 h-4" style={{ color: "var(--mp-copper)" }} />
            <span
              className="text-xs font-semibold uppercase tracking-[0.18em]"
              style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}
            >
              Compare tray · {pinned.length}/{MAX_PINS}
            </span>
          </span>
          <span className="flex items-center gap-3">
            <span className="hidden sm:flex items-center gap-1">
              {pinned.map((p) => (
                <span
                  key={p.key}
                  className="w-4 h-4 rounded-sm border border-[var(--mp-line)]"
                  style={{ backgroundColor: p.colour_hex }}
                />
              ))}
            </span>
            <ChevronDown
              className="w-4 h-4 transition-transform duration-300"
              style={{
                color: "var(--mp-muted)",
                transform: open ? "none" : "rotate(180deg)",
              }}
            />
          </span>
        </button>

        <div
          className="transition-all duration-400 ease-out overflow-hidden"
          style={{ maxHeight: open ? (shareCard ? 400 : 280) : 0, opacity: open ? 1 : 0 }}
        >
          <div className="p-5">
            <div className="flex gap-3 overflow-x-auto pb-2" data-testid="compare-tray-cards">
              {pinned.map((pin) => (
                <PinCard key={pin.key} pin={pin} onRemove={onRemove} />
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <Button
                onClick={onAddAll}
                className="rounded-sm text-xs font-semibold px-5"
                style={{
                  backgroundColor: "var(--mp-copper-solid)",
                  color: "#FFFFFF",
                  fontFamily: "'Manrope', sans-serif",
                }}
                data-testid="compare-add-all"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add all to order
              </Button>
              <Button
                onClick={onShare}
                disabled={sharing}
                className="rounded-sm text-xs font-semibold px-5"
                style={{
                  backgroundColor: "#1EA952",
                  color: "#FFFFFF",
                  fontFamily: "'Manrope', sans-serif",
                }}
                data-testid="compare-share"
              >
                <Share2 className="w-3.5 h-3.5 mr-1.5" />
                {sharing ? "Building card…" : "Share on WhatsApp"}
              </Button>
              <button
                onClick={onClear}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-4 py-2 rounded-sm border"
                style={{ borderColor: "var(--mp-line)", color: "var(--mp-muted)" }}
                data-testid="compare-clear"
              >
                <Trash2 className="w-3.5 h-3.5" /> Clear tray
              </button>
              <p className="text-[11px]" style={{ color: "var(--mp-muted)" }}>
                Pinned shades stay here while you browse families — best viewed in Showroom mode.
              </p>
            </div>

            {shareError && (
              <p
                className="mt-3 text-[11px] font-medium"
                style={{ color: "#DC2626" }}
                data-testid="compare-share-error"
              >
                {shareError}
              </p>
            )}

            {shareCard && (
              <div
                className="mt-4 flex items-center gap-4 p-3 rounded-sm border"
                style={{ borderColor: "var(--mp-line)", backgroundColor: "var(--mp-surface)" }}
                data-testid="compare-share-result"
              >
                <img
                  src={shareCard.url}
                  alt="Shade shortlist card"
                  className="h-16 w-auto rounded-sm border border-[var(--mp-line)]"
                />
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-[11px]" style={{ color: "var(--mp-muted)" }}>
                    Your shortlist card is ready.
                  </span>
                  <a
                    href={shareCard.url}
                    download="mutha-paras-shade-shortlist.png"
                    className="text-[11px] font-semibold uppercase tracking-wider"
                    style={{ color: "var(--mp-copper)" }}
                    data-testid="compare-share-download"
                  >
                    Download card
                  </a>
                  <a
                    href={`https://wa.me/917624853840?text=${encodeURIComponent(shareCard.text)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] font-semibold uppercase tracking-wider"
                    style={{ color: "#1EA952" }}
                    data-testid="compare-share-whatsapp-again"
                  >
                    Open WhatsApp
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
